import { isNilOrEmpty } from '../../language/module';
import { PropValidationMsg } from '../../forms/validation/propValidationMsg';
import { UserAr } from './userAr';
import { makeRule, PropValidationRule, PropValidator } from '../../forms/validation/propValidation';
import * as R from 'ramda';
import { getResource } from '../../language/resources/getResource';
import { MAX_USERNAME_LENGTH, MIN_USERNAME_LENGTH, USER_FIELD_BLACKLIST_CHARS } from './constants';


/**
 * Regex for whether or not a complete string is a valid email address (ignoring some of the craziest possible emails)
 */
export const EMAIL_REGEX = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,8}$';


/**
 * Username
 * Criteria:
 * - Unique per resort (can't use RegEx for that, but documenting here for posterity)
 * - Min length: 4
 * - Max length: 20
 */
export const USERNAME_REGEX = `^[a-zA-Z0-9_]{${MIN_USERNAME_LENGTH},${MAX_USERNAME_LENGTH}}$`;


/**
 * Password
 * Criteria:
 * - Min length: 8
 * - One uppercase letter
 * - One lowercase letter
 * - One number
 *
 * Regex from: https://www.thepolyglotdeveloper.com/2015/05/use-regex-to-test-password-strength-in-javascript/
 */
export const PASSWORD_REGEX = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})';

export const removeBlacklistedCharacters = (value: string): string => {
  let result = value;
  USER_FIELD_BLACKLIST_CHARS.forEach(char => result = result.replaceAll(char, ''));
  return result;
};

/**
 * Validates a UserAr's userName prop. Requires you to pass extras with `extras.usernames` string[] prop.
 */
export const validateUsername: PropValidationRule<UserAr> = (user, extras): PropValidationMsg | undefined => {

  if (!user || isNilOrEmpty(user.userName)) {
    return { propName: 'userName', msg: getResource(extras.resources, 'USER_FIELD_REQUIRED_VALIDATION') };
  }

  if (!extras.userNameAvailable) {
    return { propName: 'userName', msg: getResource(extras.resources, 'USER_NAME_NOT_AVAILABLE') };
  }

  if (!RegExp(USERNAME_REGEX).exec(user.userName)) {
    return {
      propName: 'userName',
      msg:      getResource(extras.resources, 'USER_NAME_FORMAT_VALIDATION'),
    };
  }

  return undefined;
};

export const isValidEmail: PropValidationRule<UserAr> = (user, extras): PropValidationMsg | undefined => {
  if (!user || isNilOrEmpty(user.email)) {
    return undefined;
  }

  if (!RegExp(EMAIL_REGEX).exec(user.email)){
    return {
      msg: getResource(extras.resources, 'USER_EMAIL_FORMAT_VALIDATION'),
      propName: 'email'
    }
  }

  if (!extras.emailAvailable) {
    return { propName: 'email', msg: getResource(extras.resources, 'USER_EMAIL_NOT_AVAILABLE') };
  }

  return undefined;
};

export const hasValidLastName: PropValidationRule<UserAr> = makeRule(
  (user) => isNilOrEmpty(user.lastName.trim()),
  (extras) => getResource(extras.resources, 'USER_FIELD_REQUIRED_VALIDATION'),
  'lastName',
);

export const lastNameHasNoBlacklistedCharacters : PropValidationRule<UserAr> = makeRule(
  (user) =>  USER_FIELD_BLACKLIST_CHARS.some((token) => user.lastName.includes(token)), 
  (extras) => getResource(extras.resources, 'USER_FIELD_INVALID_CHARACTERS', USER_FIELD_BLACKLIST_CHARS.join(',')), 
  'lastName'
);

export const hasValidFirstName: PropValidationRule<UserAr> = makeRule(
  (user) => isNilOrEmpty(user.firstName.trim()),
  (extras) => getResource(extras.resources, 'USER_FIELD_REQUIRED_VALIDATION'),
  'firstName',
);

export const firstNameHasNoBlacklistedCharacters : PropValidationRule<UserAr> = makeRule(
  (user) =>  USER_FIELD_BLACKLIST_CHARS.some((token) => user.firstName.includes(token)), 
  (extras) => getResource(extras.resources, 'USER_FIELD_INVALID_CHARACTERS', USER_FIELD_BLACKLIST_CHARS.join(',')), 
  'firstName'
);

/**
 * PropValidationRule for UserAr Roles
 *
 * Requires extras in the shape:
 * {
 *   roles: string[],
 * }
 */
export const hasValidRole: PropValidationRule<UserAr> = makeRule(
  (user, extras) => !R.includes(user.role, extras.roles),
  (extras) => getResource(extras.resources, 'USER_FIELD_REQUIRED_VALIDATION'),
  'role',
);


export const hasValidResortLocation: PropValidationRule<UserAr> = makeRule(
  (user) => isNilOrEmpty(user.resortLocations),
  (extras) => getResource(extras.resources, 'USER_FIELD_REQUIRED_VALIDATION'),
  'resortLocations',
);


/**hasValid
 * PropValidationRule for UserAr Passwords
 *
 * Requires extras in the shape:
 * {
 *   validatePassword: boolean,
 * }
 */
export const hasValidPassword: PropValidationRule<UserAr> = makeRule(
  (user, extras) => extras.validatePassword
                    && (!user || isNilOrEmpty(user.password) || !RegExp(PASSWORD_REGEX).exec(user.password)),
  (extras) => getResource(extras.resources, 'USER_PASSWORD_FORMAT_VALIDATION'),
  'password',
);


/**
 * Returns an array of validation message objects for things we don't like about the given UserAr object.
 * Requires extras in the shape:
 * {
 *   usernames: string[],
 *   roles: string[],
 *   validatePassword: boolean,
 * }
 */
export const validateNewUser: PropValidator<UserAr> = (user, extras) => [
  validateUsername,
  isValidEmail,
  hasValidFirstName,
  firstNameHasNoBlacklistedCharacters,
  hasValidLastName,
  lastNameHasNoBlacklistedCharacters,
  hasValidRole,
  hasValidResortLocation,
  hasValidPassword,
].map(rule => rule(user, extras))
  .filter(msg => !!msg) as PropValidationMsg[];
